import { getPickedEmployee } from '@/helpers/employee';
import { hasFeatureSetting } from '@/helpers/placeHelper';
import { useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { _t, i18n } from '@/locale';
import { BookState } from '@/types/state/book';
import { PlaceServiceGroup } from '@/types/state/shared';
import { CAMPAIGNS_CATEGORY_ID, getBookableCategories, getCampaignCategory } from './SelectService.helpers';
import { SelectServiceCategory, SelectServiceProps } from './SelectService.types';
import { resolvePriceListId, toSelectServiceItem } from '../serviceItem.helpers';

type SelectServiceHandlers = Pick<SelectServiceProps, 'onPickService' | 'onShowServiceInfo' | 'onHideServiceInfo'>;
type SelectServiceOptions = SelectServiceHandlers & {
  /**
   * Narrows the bookable pool before the accordion state is resolved. A caller that offers a
   * subset (the calendar modal offers only what can join the current selection) must narrow
   * here rather than filter the returned categories: the only-category and first-category
   * rules have to see the list the user actually gets, or every section opens collapsed.
   */
  includeService?: (serviceId: number) => boolean;
};
type SelectServiceHookResult = Pick<
  SelectServiceProps,
  'isNarrow' | 'categories' | 'onPickService' | 'onShowServiceInfo' | 'onHideServiceInfo'
>;

/**
 * Derives the presentational props for SelectService from the existing
 * BookAppointment Redux state (state.book). The price / duration / campaign /
 * wellness resolution is ported unchanged from the legacy pick-service row so the
 * displayed values match SummaryView; only the data-flow boundary moves here.
 */
export function useSelectService({ includeService, ...handlers }: SelectServiceOptions): SelectServiceHookResult {
  const { isMobileView } = useMobileView();
  const { place, employee, services: pickedServices = [] } = useAppSelector((state) => state.book) as BookState;

  const pickedEmployee = employee != null ? getPickedEmployee(employee, place?.employees ?? []) : undefined;

  const priceListId = resolvePriceListId(pickedEmployee);

  const bookableCategories = getBookableCategories(place, pickedServices ?? [], pickedEmployee)
    .map((category) => ({
      ...category,
      services: includeService ? category.services.filter((service) => includeService(service.id)) : category.services,
    }))
    .filter((category) => category.services.length > 0);
  const campaignCategory = getCampaignCategory(place, bookableCategories);
  const allCategories = campaignCategory ? [campaignCategory, ...bookableCategories] : bookableCategories;
  const hasCollapsedSetting = hasFeatureSetting(place, 'collapsed_services');

  const resolveDefaultExpanded = (index: number): boolean => {
    if (allCategories.length === 1) return true;
    if (hasCollapsedSetting) return false;
    if (index === 0) return true;
    return campaignCategory != null && index === 1;
  };

  const resolveCategoryName = (category: PlaceServiceGroup): string => {
    if (category.id === CAMPAIGNS_CATEGORY_ID) return _t('features.bookingSelection.SelectService.campaignsCategory');
    if (!category.name) return '';
    // `own` categories carry the salon's literal name; the rest carry a server-provided
    // translation key resolved at runtime (so the typed _t() literal-key form can't apply).
    return category.own === true ? category.name : (i18n.t(category.name) as string);
  };

  const categories: SelectServiceCategory[] = allCategories.map((category, index) => ({
    id: category.id,
    name: resolveCategoryName(category),
    services: category.services.map((service) => toSelectServiceItem(service, place, priceListId, false)),
    icon: category.id === CAMPAIGNS_CATEGORY_ID ? { variant: 'campaign-filled', color: 'fg_highlight' } : undefined,
    defaultExpanded: resolveDefaultExpanded(index),
  }));

  return {
    isNarrow: isMobileView,
    categories,
    ...handlers,
  };
}
